import * as React from "react";
import { Typography, makeStyles } from "@material-ui/core";
import { ErrorMessage, getIn, Field as FormikField } from "formik";
import { InnerContext } from "./FormInner";

const useStyles = makeStyles(theme => ({
  errMsg: {
    margin: "4px 14px 0",
    color: theme.palette.secondary.main
  }
}));

function fieldToTextField(
  {
    disabled,
    field: { onBlur: fieldOnBlur, ...field },
    form: { isSubmitting, touched, errors },
    onBlur,
    helperText,
    ...props
  },
  extraDisabled
) {
  const fieldError = getIn(errors, field.name);
  const showError = getIn(touched, field.name) && !!fieldError;

  return {
    error: showError,
    // helperText: showError ? fieldError : helperText,
    disabled: (disabled || extraDisabled) ?? isSubmitting,
    onBlur:
      onBlur ??
      function (e) {
        fieldOnBlur(e ?? field.name);
      },
    ...field,
    ...props
  };
}

export const strategies = {
  tel: (value, rule) => {
    if (value && !/^1[3456789]\d{9}$/.test(value)) {
      throw new Error(rule.message);
    }
  },
  email: (value, rule) => {
    const reg =
      /^[A-Za-z0-9]+([_.][A-Za-z0-9]+)*@([A-Za-z0-9-]+\.)+[A-Za-z]{2,6}$/;
    if (value && !reg.test(value)) {
      throw new Error(rule.message);
    }
  },
  length: (value, rule) => {
    if (typeof value === "string" || Array.isArray(value)) {
      if (!rule.params) {
        if (!value.length) {
          throw new Error(rule.message);
        }
      } else if (Array.isArray(rule.params)) {
        if (rule.params[0] && value.length < rule.params[0]) {
          throw new Error(rule.message);
        }
        if (rule.params[1] && value.length > rule.params[1]) {
          throw new Error(rule.message);
        }
      }
    }
  }
};

export function registryStrategy(strategyName, strategyHandler) {
  strategies[strategyName] = strategyHandler;
  return strategies;
}

function wrapValidate(validate, rules) {
  if (!validate && !rules) return undefined;
  if (typeof validate === "function") {
    if (!rules) rules = [];
    rules.push({ validator: validate });
  }
  if (!Array.isArray(rules)) {
    rules = [rules];
  }
  return value => {
    try {
      for (let rule of rules) {
        if (rule.required) {
          if (!rule.whiteSpace && typeof value === "string") {
            value = value.trim();
          }
          if (
            value === "" ||
            value === null ||
            value === undefined ||
            !value.length
          ) {
            throw new Error(rule.message || "This field is required.");
          }
        }
        if (rule.pattern && rule.pattern instanceof RegExp) {
          if (!rule.pattern.test(value)) {
            throw new Error(rule.message);
          }
        }
        if (rule.strategy && strategies.hasOwnProperty(rule.strategy)) {
          const msg = strategies[rule.strategy](value, rule);
          if (msg) {
            throw new Error(msg);
          }
        }
        if (rule.validator) {
          const msg = rule.validator(value, rule);
          if (msg) {
            throw new Error(msg);
          }
        }
      }
    } catch (e) {
      return e.message || "Failed to validate.";
    }
  };
}

export function Field({
  children,
  name,
  disabled,
  innerRef,
  validate,
  rules,
  ...others
}) {
  const classes = useStyles();
  const innerContext = React.useContext(InnerContext);

  return (
    <FormikField
      name={name}
      innerRef={innerRef}
      validate={wrapValidate(validate, rules)}
      render={fieldProps => {
        const origChange = fieldProps.field.onChange;
        const origBlur = fieldProps.field.onBlur;
        const numberValue = typeof fieldProps.meta.initialValue === "number";
        const child = children ? React.Children.only(children) : null;

        fieldProps.field.onChange = e => {
          // refer to checkbox and radio
          if (numberValue) {
            e = {
              target: {
                name: e.target.name,
                value: +e.target.value
              }
            };
          }
          innerContext.onValuesChange?.(e);
          child.props.onChange?.(e);
          origChange?.(e);
        };

        fieldProps.field.onBlur = e => {
          child.props.onBlur?.(e);
          origBlur?.(e);
        };

        return child ? (
          <>
            {React.cloneElement(child, {
              ...fieldToTextField(fieldProps, disabled || innerContext.disabled)
            })}
            <ErrorMessage
              name={name}
              component={Typography}
              variant="body2"
              className={classes.errMsg}
            />
          </>
        ) : null;
      }}
      {...others}
    />
  );
}
